/* endPanels.js — derived FINISHED END-PANEL detection (v0.53). DERIVED, NEVER PERSISTED.
 *
 * The first PRODUCT consumer of the RunGraph (runs.js). Professional CAD (Cabinet
 * Vision / 2020 / Microvellum) finishes a cabinet run's EXPOSED end with a decor end
 * panel; an end tucked into a wall-return corner gets a scribe strip instead, and an
 * end that turns a corner into another run gets a mitre/return — neither needs a
 * finished panel. This module derives which run ends are genuinely exposed from the
 * EXISTING placement model + wall topology. Nothing is written back: no schema change,
 * no placement change, no UI/serialization change. Consumers ask; the layer answers.
 *
 * WHAT COUNTS AS EXPOSED — we work per PHYSICAL end (the LEFT/right edge of each member)
 * and ask whether that end is CONNECTED to something within the same run. A physical end
 * needs a finished panel iff BOTH:
 *   (a) it is not connected within the run — neither a same-wall flush neighbour on that
 *       side NOR a corner transition that lands on that end's wall node. An interior
 *       member has both ends connected (no panels); a terminal has exactly one connected
 *       end (its inner end) and one free end (its OUTER, exposed end); a lone cabinet has
 *       two free ends. This is derived from run.members + run.transitions + geometry, so
 *       it is orientation-INDEPENDENT: it does not assume members[0].left / members[last].
 *       right are the outer ends. That assumption breaks at the [0,0] corner, where a
 *       run's wall-declaration order (w1 then w4) runs OPPOSITE the geometric chain and
 *       both wall-order terminals land ON the shared corner node. Reading connectivity
 *       from topology instead makes the corner-turn (mitre/return) exclusion fall out
 *       naturally on the CORRECT end, and correctly handles UPPER-run tall terminals.
 *   (b) it is not tucked flush into a wall-return corner — the end's outer edge is not
 *       within END_PANEL_CORNER_TOL_M of the wall inner-corner (a flush end gets a
 *       scribe). The inner-corner inset only EXISTS when wallInnerSpan reports off > 0
 *       (a connected return); at a free/open wall end off is 0, so the guard keeps the
 *       most-exposed case (a cabinet sitting near absLeft 0 at an OPEN end) exposed.
 *   (c) it is not BURIED in another wall's BODY (Iteration 21 tee/cross fix). A run
 *       that dead-ends INTO a crossing wall at a tee/cross junction — or against a
 *       divider's body mid-wall — reads as un-connected under (a): the crossing wall
 *       contributes only SEGMENTS to the T-split node (never wall ends, wallGraph
 *       step 2), so runs.js finds no corner-transition candidate there; and (b) never
 *       fires because wallInnerSpan tests wall-ENDPOINT coincidence only. The probe is
 *       GEOMETRIC (containment in the crossing wall's oriented thickness rectangle),
 *       NOT a junction-kind check, deliberately: it also catches a host-wall run
 *       dead-ending against a teeing wall's body mid-wall (a split node the host has
 *       no end at), and keeps a FAR-SIDE tee (crossing wall outside the room, on the
 *       other side of the member's wall) correctly exposed.
 *
 * A tall unit is a member of BOTH the base and upper runs, so a tall run-end would
 * emit two panels for one physical panel — deduped by (atPlacementId, end), preferring
 * the base line. heightMm/depthMm come from effDef (metres → mm, ×1000 rounded).
 *
 * Cost: dominated by the single buildRunGraph pass (getWallGraph memoized) — sub-ms at
 * the 100-wall / 1000-cabinet target. Build once per interaction and reuse.
 */
import { buildRunGraph, RUN_GAP_TOL } from "./runs";
import { wallInnerSpan } from "./snapping";
import { effDef } from "./pricing";
import { getWallGraph } from "./wallGraph";
import { getWallPlacementInfo } from "./placement";
import { RULE_EPS, WALL_THICKNESS } from "./constants";

export const END_PANEL_CORNER_TOL_M = 0.05; // flush-into-return tolerance (WORKTOP_CORNER_TOL family)

const BODY_PROBE_EPS_M = 0.001; // 1 mm — probe sits just room-side of the member wall's inner face

/** (c) burial probe (Iteration 21): is this physical end's face buried in another
 *  wall's BODY? Probe point P = wall.start + dir·(endAlong ± END_PANEL_CORNER_TOL_M)
 *  + placementNormal·(WT/2 + ε) — just PAST the end face along the wall, on the
 *  cabinet's (room) side of the wall line (getWallPlacementInfo's dir/normal, the
 *  same frame absLeft placement uses). Suppress when P falls inside any OTHER
 *  straight wall's oriented thickness rectangle. Crossing bodies are ≥ WT (0.15 m)
 *  thick, so a single probe at reach END_PANEL_CORNER_TOL_M (0.05 m) catches every
 *  gap ≤ tol. Arcs are skipped (chord containment would lie about the band);
 *  peninsulas are real bodies — included. WALL_THICKNESS is read at call time
 *  (live mutable binding, LOCK-W-08). */
function endBuriedInWallBody(m, end, walls) {
  const info = getWallPlacementInfo(m.wall, walls);
  if (!info) return false;
  const along = end === "left"
    ? m.left - END_PANEL_CORNER_TOL_M
    : m.left + m.width + END_PANEL_CORNER_TOL_M;
  const half = WALL_THICKNESS / 2;
  const px = m.wall.start[0] + info.dirX * along + info.nx * (half + BODY_PROBE_EPS_M);
  const pz = m.wall.start[1] + info.dirZ * along + info.nz * (half + BODY_PROBE_EPS_M);
  for (const w2 of walls) {
    if (!w2 || w2.id === m.wall.id || w2.type === "arc") continue;
    if (!Array.isArray(w2.start) || !Array.isArray(w2.end)) continue;
    const dx = w2.end[0] - w2.start[0], dz = w2.end[1] - w2.start[1];
    const L = Math.hypot(dx, dz);
    if (L < 0.01) continue; // engine-wide degenerate-wall threshold (MIN_WALL_LEN)
    const ux = dx / L, uz = dz / L;
    const rx = px - w2.start[0], rz = pz - w2.start[1];
    const t = rx * ux + rz * uz;
    if (t < 0 || t > L) continue;                     // outside the body along its axis
    if (Math.abs(rx * -uz + rz * ux) <= half) return true; // inside the thickness band
  }
  return false;
}

/** buildEndPanels(walls, placements, lib) → EndPanel[].
 *  EndPanel = { wallId, runId, end: "left"|"right", atPlacementId, role: "base"|"upper",
 *  heightMm, depthMm, reason }. DERIVED, pure, no mutation. Deterministic order
 *  (wallId, then end, then atPlacementId, then runId). */
export function buildEndPanels(walls, placements, lib) {
  const wls = walls || [];
  const pls = placements || [];
  const rg = buildRunGraph(pls, lib, wls);
  const graph = getWallGraph(wls); // memoized — same instance runs.js consumed, so node ids match
  const byId = new Map(pls.map((p) => [p.id, p]));
  const wallById = new Map(wls.map((w) => [w.id, w]));

  // Candidates keyed by (atPlacementId|end): a dual-line tall terminal produces one
  // base + one upper candidate for a single physical panel — dedupe, preferring base.
  const byKey = new Map();

  rg.runs.forEach((run) => {
    const members = run.members || [];
    if (!members.length) return;

    // Resolve every member's geometry once; skip anything unresolvable (defensive —
    // buildRunGraph already filtered to resolvable members, but a lib mismatch is cheap
    // to guard). connLeft/connRight track whether each PHYSICAL end is joined in-run.
    const info = new Map();
    members.forEach((id) => {
      const p = byId.get(id);
      if (!p) return;
      const def = effDef(p, lib);
      if (!def) return;
      const wall = wallById.get(p.wallId);
      if (!wall) return;
      const wi = graph.walls.get(p.wallId);
      if (!wi) return;
      info.set(id, {
        p, def, wall, wi,
        left: p.absLeft || 0,
        width: def.width || 0,
        connLeft: false,
        connRight: false,
      });
    });
    if (!info.size) return;

    // (a-i) Same-wall flush adjacency (the run's chain segments). Mirrors runs.js:
    // a flush pair (gap ∈ [−RULE_EPS, RUN_GAP_TOL]) joins the left member's RIGHT end
    // and the right member's LEFT end. Sorted per wall so `break` is safe.
    const byWall = new Map();
    info.forEach((m, id) => {
      if (!byWall.has(m.p.wallId)) byWall.set(m.p.wallId, []);
      byWall.get(m.p.wallId).push(id);
    });
    byWall.forEach((ids) => {
      ids.sort((a, b) => info.get(a).left - info.get(b).left);
      for (let x = 0; x < ids.length; x++) {
        const a = info.get(ids[x]);
        for (let y = x + 1; y < ids.length; y++) {
          const b = info.get(ids[y]);
          const gap = b.left - (a.left + a.width);
          if (gap > RUN_GAP_TOL) break;   // sorted — nothing later is closer
          if (gap < -RULE_EPS) continue;  // true overlap (invalid state) — not a chain
          a.connRight = true;
          b.connLeft = true;
        }
      }
    });

    // (a-ii) Corner-turn adjacency: a transition joins a member at whichever of its two
    // ends sits on the transition's wall node (startNode ⇒ left end, endNode ⇒ right end).
    // This marks the CORRECT geometric end regardless of wall-declaration order.
    (run.transitions || []).forEach((t) => {
      [t.aId, t.bId].forEach((id) => {
        const m = info.get(id);
        if (!m) return;
        if (t.nodeId === m.wi.startNode) m.connLeft = true;
        else if (t.nodeId === m.wi.endNode) m.connRight = true;
      });
    });

    // Each UN-connected physical end is an exposed candidate (interior members have both
    // ends connected and emit nothing; a lone cabinet has two free ends).
    info.forEach((m, id) => {
      (["left", "right"]).forEach((end) => {
        if (end === "left" ? m.connLeft : m.connRight) return; // joined in-run, not exposed

        // (b) tucked flush into a wall-return corner (only when the inset actually exists).
        const span = wallInnerSpan(m.wall, wls);
        const tol = END_PANEL_CORNER_TOL_M;
        const abutsReturn = end === "left"
          ? span.off0 > 0 && m.left <= span.off0 + tol
          : span.off1 > 0 && m.left + m.width >= span.len - span.off1 - tol;
        if (abutsReturn) return; // scribe, not a finished end panel — "abuts-return" excluded

        // (c) Iteration 21 tee/cross over-emit fix: an end face buried in another
        // wall's body (tee / cross / mid-wall divider abut) is not exposed.
        if (endBuriedInWallBody(m, end, wls)) return;

        const panel = {
          wallId: m.p.wallId,
          runId: run.id,
          end,
          atPlacementId: id,
          role: run.line, // "base" | "upper"
          heightMm: Math.round((m.def.height || 0) * 1000),
          depthMm: Math.round((m.def.depth || 0) * 1000),
          reason: "exposed-run-end",
        };
        const key = id + "|" + end;
        const prev = byKey.get(key);
        // Keep first; a full-height tall panel is identical either line — prefer base.
        if (!prev || (prev.role === "upper" && panel.role === "base")) byKey.set(key, panel);
      });
    });
  });

  return [...byKey.values()].sort((a, b) => {
    if (a.wallId !== b.wallId) return a.wallId < b.wallId ? -1 : 1;
    const er = (a.end === "left" ? 0 : 1) - (b.end === "left" ? 0 : 1);
    if (er !== 0) return er;
    if (a.atPlacementId !== b.atPlacementId) return a.atPlacementId < b.atPlacementId ? -1 : 1;
    return a.runId < b.runId ? -1 : a.runId > b.runId ? 1 : 0;
  });
}
